import { useCallback, useState } from 'react'

import { Accordion, Checkbox } from '@loadsmart/loadsmart-ui'

import { Icon } from 'core/ui/components/icon/Icon'

import { type LoadOffer } from '../../domain/LoadOffer'
import { getNextStep, getStepIndexByStepId, SupportedSteps } from '../../domain/LoadSetupSteps'
import { type Shipment } from '../../domain/shipment/Shipment'
import { AccordionBodyWrapper } from './LoadSetup.styles'
import { DriverStep } from './steps/DriverStep'
import { TrackingIntegrationStep } from './steps/TrackingIntegrationStep'
import { TruckInfoAndLocationStep } from './steps/TruckInfoAndLocationStep'

type LoadSetupProps = {
  loadOffer: LoadOffer
  shipment: Shipment
}

const loadSetupSteps = [
  {
    id: SupportedSteps.TrackingIntegration,
    title: 'Allow Tracking',
    Component: TrackingIntegrationStep,
  },
  {
    id: SupportedSteps.Driver,
    title: 'Select Driver',
    Component: DriverStep,
  },
  {
    id: SupportedSteps.TruckInfo,
    title: 'Truck info and location',
    Component: TruckInfoAndLocationStep,
  },
]

export function LoadSetup({ loadOffer, shipment }: LoadSetupProps) {
  const [currentLoadSetupStep, setCurrentLoadSetupStep] = useState<number>(0)
  const [completedSteps, setCompletedSteps] = useState<Array<SupportedSteps>>([])

  function handleNextStep(id: SupportedSteps) {
    const nextStep = getNextStep(id, loadSetupSteps)
    if (nextStep < loadSetupSteps.length) {
      setCurrentLoadSetupStep(nextStep)
    }
  }

  const handleMarkAsComplete = useCallback((step: SupportedSteps) => {
    setCompletedSteps(completeStep => {
      if (!completeStep.includes(step)) {
        return [...completeStep, step]
      }

      return completeStep
    })
  }, [])

  const handleStepComplete = useCallback(() => {
    const currentStep = loadSetupSteps[currentLoadSetupStep].id
    if (currentStep) {
      handleMarkAsComplete(currentStep)

      handleNextStep(currentStep)
    }
  }, [currentLoadSetupStep, handleMarkAsComplete])

  const handleExpandChange = useCallback((step: SupportedSteps, expanded: boolean) => {
    if (expanded) {
      const stepIndex = getStepIndexByStepId(step, loadSetupSteps)

      if (stepIndex > -1) {
        setCurrentLoadSetupStep(stepIndex)
      }
    }
  }, [])

  return (
    <>
      {loadSetupSteps.map((step, index) => {
        const isCompleted = completedSteps.includes(step.id)
        const isCurrentStep = index === currentLoadSetupStep

        return (
          <Accordion
            key={step.id}
            expanded={isCurrentStep}
            onExpandedChange={expanded => handleExpandChange(step.id, expanded)}
          >
            <Accordion.Toggle
              leading={<Checkbox checked={isCompleted} onChange={() => {}} />}
              trailing={(expanded: boolean) => (
                <Icon name={expanded ? 'arrows-expand-side' : 'arrows-expanded'} />
              )}
            >
              {`${index + 1} - ${step.title}`}
            </Accordion.Toggle>
            <AccordionBodyWrapper $overflow={isCurrentStep ? 'initial' : 'auto'}>
              <Accordion.Body>
                <step.Component
                  id={step.id}
                  loadOffer={loadOffer}
                  onComplete={handleStepComplete}
                  onMarkAsComplete={() => handleMarkAsComplete(step.id)}
                  shipment={shipment}
                />
              </Accordion.Body>
            </AccordionBodyWrapper>
          </Accordion>
        )
      })}
    </>
  )
}
